// src/components/FinancialIntelligence/FIOutputCanvas.js
//
// Right panel output area for Financial Intelligence.
// Shows: streaming output during generation, persisted output from selected entry.
// Includes: follow-up suggestion chips, export button (PDF/Word).
// ReactMarkdown rendering matches ExistingQuestions pattern.

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating,
  Tooltip,
  IconButton,
  Paper,
} from '@mui/material';
import {
  FileDownload as ExportIcon,
  AccountBalance as FIIcon,
  Autorenew as StreamingIcon,
  EditNote as EditNoteIcon,
  Feedback as FeedbackIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { generateGovernanceHeader, generateGovernanceFooter } from '../../utils/governanceExport';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import ArtifactReviewPanel from '../ArtifactReviewPanel';

// ── Streaming phase labels (matches QuestionsSection pattern) ──
const PHASE_LABELS = {
  assembling_context: 'Assembling client context…',
  streaming_answer:   'Analyzing…',
  persisting_results: 'Saving analysis…',
  done:               'Complete',
};

const FIOutputCanvas = ({
  activeEntry,
  streamingText,
  streamingPhase,
  streamingPrompt,
  followUpSuggestions,
  onFollowUpClick,
  clientId,
  client,
  user,
}) => {
  const [exportAnchor, setExportAnchor]   = useState(null);
  const [exporting, setExporting]         = useState(false);

  // Note dialog
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteText, setNoteText]             = useState('');
  const [savingNote, setSavingNote]         = useState(false);

  // Feedback dialog
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackRating, setFeedbackRating]         = useState(0);
  const [feedbackText, setFeedbackText]             = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  // Share dialog
  const [shareDialogOpen, setShareDialogOpen]   = useState(false);
  const [shareRecipient, setShareRecipient]     = useState('');
  const [shareMessage, setShareMessage]         = useState('');
  const [sharing, setSharing]                   = useState(false);

    const isStreaming = !!streamingPhase && streamingPhase !== 'done';
  const showStreamingOutput = !!streamingText && (!activeEntry || !!streamingPhase);
  const hasOutput   = showStreamingOutput ? true : !!activeEntry?.outputMarkdown;
  const rawOutputText = showStreamingOutput ? streamingText : (activeEntry?.outputMarkdown || '');
  const outputText = rawOutputText.replace(/^##\s+Suggested Follow-Ups[\s\S]*$/im, '').trimEnd();
  const visiblePrompt = isStreaming
    ? (streamingPrompt || '')
    : (activeEntry?.rawPrompt || '');
  const visibleFollowUps = showStreamingOutput
    ? followUpSuggestions
    : (activeEntry?.followUpSuggestions || []);

  // ── Client-side generators (Word / CSV / JSON) ──────────────────────────────
  // Matches the existing AI Governance export pattern in UserPortalAIGovernance.js.
  // PDF is the only format that calls a Cloud Function (returns pdfBase64).

  const _generateWordDoc = async () => {
    const clientName = client?.displayName || client?.name || 'Client';
    const analysisDate = activeEntry.createdAt?.toDate?.() || new Date();

    const govHeader = generateGovernanceHeader({
      threadId:       activeEntry.id,
      clientName,
      contentType:    'Financial Intelligence',
      dateRangeStart: activeEntry.createdAt,
      dateRangeEnd:   activeEntry.createdAt,
      exportTimestamp: new Date(),
      aiProviders: [
        { name: 'Claude (Anthropic)',   role: 'primary analysis' },
        { name: 'GPT-4.1-mini (OpenAI)', role: 'routing & summarization' },
      ],
    });

    const contentParagraphs = _markdownToDocxParagraphs(activeEntry.outputMarkdown || '');

    // Follow-up suggestions
    const followUpParagraphs = [];
    if (activeEntry.followUpSuggestions?.length > 0) {
      followUpParagraphs.push(
        new Paragraph({ children: [new TextRun({ text: 'Suggested Follow-Ups', bold: true, size: 20, color: '005DAA' })], spacing: { before: 200, after: 80 } }),
        ...activeEntry.followUpSuggestions.map(s =>
          new Paragraph({ children: [new TextRun({ text: `• ${s}`, size: 18, color: '555555' })], spacing: { after: 60 } })
        )
      );
    }

    const govFooter = generateGovernanceFooter();

    return Packer.toBlob(new Document({
      sections: [{ children: [...govHeader, ...contentParagraphs, ...followUpParagraphs, ...govFooter] }],
    }));
  };

  const _markdownToDocxParagraphs = (markdown) => {
    const paragraphs = [];
    for (const line of markdown.split('\n')) {
      const t = line.trim();
      if (!t) { paragraphs.push(new Paragraph({ text: '', spacing: { after: 60 } })); continue; }
      if (t === '---') {
        paragraphs.push(new Paragraph({ text: '', border: { bottom: { style: 'single', size: 4, color: 'CCCCCC' } }, spacing: { before: 80, after: 80 } }));
        continue;
      }
      if (t.startsWith('## ')) {
        paragraphs.push(new Paragraph({ children: [new TextRun({ text: t.slice(3), bold: true, size: 24, color: '005DAA' })], spacing: { before: 240, after: 80 } }));
        continue;
      }
      if (t.startsWith('### ')) {
        paragraphs.push(new Paragraph({ children: [new TextRun({ text: t.slice(4), bold: true, size: 20 })], spacing: { before: 160, after: 60 } }));
        continue;
      }
      if (t.startsWith('- ') || t.startsWith('* ')) {
        paragraphs.push(new Paragraph({ children: _parseInlineBold(t.slice(2), 18, '1A1A1A'), bullet: { level: 0 }, spacing: { after: 60 } }));
        continue;
      }
      paragraphs.push(new Paragraph({ children: _parseInlineBold(t, 18, '1A1A1A'), spacing: { after: 80 } }));
    }
    return paragraphs;
  };

  const _parseInlineBold = (text, size, color) => {
    return text.split(/(\*\*[^*]+\*\*)/).map(part =>
      part.startsWith('**') && part.endsWith('**')
        ? new TextRun({ text: part.slice(2, -2), bold: true, size, color })
        : new TextRun({ text: part, size, color })
    );
  };

  const _generateCSVText = () => {
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Field', 'Value'],
      ['Client',            client?.displayName || client?.name || ''],
      ['Analysis Date',     activeEntry.createdAt?.toDate?.()?.toLocaleDateString() || ''],
      ['Analysis Type',     activeEntry.triggerLabel || ''],
      ['Analytical Lenses', (activeEntry.analysisLenses || []).join(', ')],
      ['Data Tier',         activeEntry.dataSnapshot?.financialIntelligenceTier || ''],
      ['QB Period',         activeEntry.dataSnapshot?.qbPeriod || ''],
      ['Governance ID',     activeEntry.governanceEventId || ''],
      ['Key Finding',       activeEntry.financialIntelligenceSummary || ''],
      ['Follow-Up 1',       activeEntry.followUpSuggestions?.[0] || ''],
      ['Follow-Up 2',       activeEntry.followUpSuggestions?.[1] || ''],
      ['Follow-Up 3',       activeEntry.followUpSuggestions?.[2] || ''],
      ['Full Output',       activeEntry.outputMarkdown || ''],
    ];
    return rows.map(r => r.map(escape).join(',')).join('\r\n');
  };

  const _generateJSONText = () => {
    return JSON.stringify({
      exportedAt:       new Date().toISOString(),
      client:           client?.displayName || client?.name || '',
      entryId:          activeEntry.id,
      triggerType:      activeEntry.triggerType,
      triggerLabel:     activeEntry.triggerLabel,
      analysisLenses:   activeEntry.analysisLenses,
      dataSnapshot:     activeEntry.dataSnapshot,
      governanceEventId: activeEntry.governanceEventId,
      financialIntelligenceSummary: activeEntry.financialIntelligenceSummary,
      followUpSuggestions: activeEntry.followUpSuggestions,
      outputMarkdown:   activeEntry.outputMarkdown,
      createdAt:        activeEntry.createdAt?.toDate?.()?.toISOString() || null,
    }, null, 2);
  };

  // ── Export ────────────────────────────────────────────────────────────────────
  const handleExport = async (format) => {
    setExportAnchor(null);
    if (!activeEntry?.id) return;

    setExporting(true);
    try {
      const clientName = client?.displayName || client?.name || 'FI-Export';
      const dateStr    = activeEntry.createdAt?.toDate?.()?.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-') || 'export';
      const fileStem   = `FI-${clientName.replace(/[^a-z0-9]/gi, '-')}-${dateStr}`;

      let blob, filename;

      if (format === 'word') {
        blob     = await _generateWordDoc();
        filename = `${fileStem}.docx`;

      } else if (format === 'csv') {
        blob     = new Blob([_generateCSVText()], { type: 'text/csv;charset=utf-8;' });
        filename = `${fileStem}.csv`;

      } else if (format === 'json') {
        blob     = new Blob([_generateJSONText()], { type: 'application/json;charset=utf-8;' });
        filename = `${fileStem}.json`;

      } else if (format === 'pdf') {
        // PDF generated server-side (matches generateGovernancePDF pattern)
        const exportFn = httpsCallable(functions, 'exportFinancialIntelligenceEntry');
        const result   = await exportFn({ entryId: activeEntry.id, clientId, format: 'pdf' });
        const data     = result.data;
        if (data?.pdfBase64) {
          const byteChars = atob(data.pdfBase64);
          const byteArray = new Uint8Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
          blob     = new Blob([byteArray], { type: 'application/pdf' });
          filename = data.filename || `${fileStem}.pdf`;
        }
      }

      if (blob && filename) {
        const url  = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Export downloaded.');
      }

    } catch (err) {
      console.error('[FIOutputCanvas] Export error:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };
    // ── Note handler ─────────────────────────────────────────────
  const handleOpenNoteDialog = () => {
    setNoteText(activeEntry?.userNotes || '');
    setNoteDialogOpen(true);
  };

  const handleSaveNote = async () => {
    if (!activeEntry?.id) return;
    setSavingNote(true);
    try {
      const entryRef = doc(db, 'clients', clientId, 'financialIntelligence', activeEntry.id);
      await updateDoc(entryRef, {
        userNotes: noteText.trim(),
      });
      toast.success('Note saved.');
      setNoteDialogOpen(false);
    } catch (err) {
      console.error('[FIOutputCanvas] Save note error:', err);
      toast.error('Failed to save note.');
    } finally {
      setSavingNote(false);
    }
  };

  // ── Feedback handlers ─────────────────────────────────────────
  const openFeedbackDialog = () => {
    setFeedbackRating(activeEntry?.feedback?.rating || 0);
    setFeedbackText(activeEntry?.feedback?.comment || '');
    setFeedbackDialogOpen(true);
  };

  const closeFeedbackDialog = () => {
    setFeedbackDialogOpen(false);
    setFeedbackRating(0);
    setFeedbackText('');
  };

  const handleFeedbackSubmit = async () => {
    if (!activeEntry?.id || feedbackRating === 0) return;
    setSubmittingFeedback(true);
    try {
      const entryRef = doc(db, 'clients', clientId, 'financialIntelligence', activeEntry.id);
      await updateDoc(entryRef, {
        feedback: {
          rating: feedbackRating,
          comment: feedbackText.trim(),
          submittedAt: serverTimestamp(),
          submittedBy: user?.email || '',
        },
      });
      toast.success('Feedback submitted.');
      closeFeedbackDialog();
    } catch (err) {
      console.error('[FIOutputCanvas] Feedback error:', err);
      toast.error('Failed to submit feedback.');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  // ── Share handler ─────────────────────────────────────────────
  const openShareDialog = () => {
    setShareRecipient('');
    setShareMessage('');
    setShareDialogOpen(true);
  };

  const closeShareDialog = () => {
    setShareDialogOpen(false);
    setShareRecipient('');
    setShareMessage('');
  };

  const handleShare = async () => {
  if (!activeEntry?.id || !shareRecipient.trim()) return;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(shareRecipient.trim())) {
    toast.error('Please enter a valid email address.');
    return;
  }

  setSharing(true);

  try {
    const shareFn = httpsCallable(functions, 'shareFinancialIntelligenceByEmail');

    await shareFn({
      recipientEmail: shareRecipient.trim(),
      personalMessage: shareMessage.trim(),
      fiData: {
        id: activeEntry.id,
        triggerType: activeEntry.triggerType || '',
        triggerLabel: activeEntry.triggerLabel || '',
        rawPrompt: activeEntry.rawPrompt || '',
        analysisLenses: activeEntry.analysisLenses || [],
        financialIntelligenceSummary: activeEntry.financialIntelligenceSummary || '',
        followUpSuggestions: activeEntry.followUpSuggestions || [],
        outputMarkdown: activeEntry.outputMarkdown || '',
        governanceEventId: activeEntry.governanceEventId || '',
        createdAt: activeEntry.createdAt || null,
      },
    });

    toast.success(`Analysis shared with ${shareRecipient.trim()}.`);
    closeShareDialog();

  } catch (err) {
    console.error('[FIOutputCanvas] Share error:', err);
    toast.error('Failed to share. Please try again.');
  } finally {
    setSharing(false);
  }
};

  // ── Word export shortcut (action row direct button) ───────────
  const handleWordExport = async () => {
    if (!activeEntry?.id || exporting) return;
    setExporting(true);
    try {
      const clientName = client?.displayName || client?.name || 'FI-Export';
      const dateStr = activeEntry.createdAt?.toDate?.()
        ?.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
        .replace(/\//g, '-') || 'export';
      const blob = await _generateWordDoc();
      const filename = `FI-${clientName.replace(/[^a-z0-9]/gi, '-')}-${dateStr}.docx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded.');
    } catch (err) {
      console.error('[FIOutputCanvas] Word export error:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };
    if (!showStreamingOutput && !activeEntry) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 6 }}>
        <FIIcon sx={{ fontSize: 52, color: 'text.disabled', mb: 2 }} />
        <Typography variant="body1" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
          Financial Intelligence
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', maxWidth: 340 }}>
          Select a situation below or ask anything about your client's financial position.
          Every analysis adds to a continuous conversation that deepens over time.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* ── Output header row ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>

        {/* Phase indicator or entry date */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {isStreaming && (
            <>
              <StreamingIcon sx={{
                fontSize: 15, color: 'primary.main',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } }
              }} />
              <Typography variant="caption" color="primary.main" fontWeight={500}>
                {PHASE_LABELS[streamingPhase] || 'Analyzing…'}
              </Typography>
            </>
          )}
          {!isStreaming && activeEntry?.createdAt && (
            <Typography variant="caption" color="text.disabled">
              {activeEntry.createdAt.toDate
                ? activeEntry.createdAt.toDate().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                : ''}
            </Typography>
          )}
        </Box>

        {/* Action row — only when not streaming and entry exists */}
        {!isStreaming && activeEntry && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>

            {/* Share */}
            <Tooltip title="Share via Email">
              <IconButton size="small" onClick={openShareDialog}>
                <ShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Note */}
            <Tooltip title={activeEntry.userNotes ? 'Edit Note' : 'Add Note'}>
              <IconButton size="small" onClick={handleOpenNoteDialog}>
                <EditNoteIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Feedback */}
            <Tooltip title={activeEntry.feedback ? 'Update Feedback' : 'Leave Feedback'}>
              <IconButton size="small" onClick={openFeedbackDialog}>
                <FeedbackIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Export dropdown (PDF / Word / CSV / JSON) */}
<Button
  size="small"
  variant="outlined"
  startIcon={exporting
    ? <CircularProgress size={12} color="inherit" />
    : <ExportIcon sx={{ fontSize: 14 }} />
  }
  endIcon={<span style={{ fontSize: 10 }}>▾</span>}
  disabled={exporting}
  onClick={(e) => setExportAnchor(e.currentTarget)}
  sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.4, px: 1.2 }}
>
  {exporting ? 'Exporting…' : 'Export'}
</Button>

<Menu
  anchorEl={exportAnchor}
  open={Boolean(exportAnchor)}
  onClose={() => setExportAnchor(null)}
>
  <MenuItem onClick={() => handleExport('pdf')}  sx={{ fontSize: '0.85rem' }}>
    Export as PDF
  </MenuItem>

  <MenuItem onClick={() => handleWordExport()}   sx={{ fontSize: '0.85rem' }}>
    Export as Word (.docx)
  </MenuItem>

  <MenuItem onClick={() => handleExport('csv')}  sx={{ fontSize: '0.85rem' }}>
    Export as CSV
  </MenuItem>

  <MenuItem onClick={() => handleExport('json')} sx={{ fontSize: '0.85rem' }}>
    Export as JSON
  </MenuItem>
</Menu>

          </Box>
        )}
      </Box>

      {/* ── Submitted prompt ── */}
      {visiblePrompt && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', mb: 0.5, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            Question
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.92rem',
              lineHeight: 1.55,
              color: 'text.primary',
              fontWeight: 500,
              whiteSpace: 'pre-wrap',
            }}
          >
            {visiblePrompt}
          </Typography>
          <Divider sx={{ mt: 1.5, mb: 0.5 }} />
        </Box>
      )}

      {/* ── Output content ── */}
      {hasOutput && (
        <Box
          sx={{
            '& h2': { fontSize: '1.05rem', fontWeight: 700, mt: 2.5, mb: 0.75, color: 'text.primary' },
            '& h3': { fontSize: '0.95rem', fontWeight: 600, mt: 2, mb: 0.5, color: 'text.primary' },
            '& p':  { fontSize: '0.9rem', lineHeight: 1.65, mb: 1, color: 'text.primary' },
            '& li': { fontSize: '0.9rem', lineHeight: 1.6, mb: 0.3 },
            '& ul, & ol': { pl: 2.5, mb: 1 },
            '& strong': { fontWeight: 600 },
            '& hr': { borderColor: 'divider', my: 2 },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {outputText}
          </ReactMarkdown>

          {/* Streaming cursor */}
          {isStreaming && streamingPhase === 'streaming_answer' && (
            <Box component="span" sx={{
              display: 'inline-block',
              width: 8, height: 16,
              bgcolor: 'primary.main',
              ml: 0.5,
              animation: 'blink 1s step-end infinite',
              '@keyframes blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } },
              verticalAlign: 'text-bottom',
            }} />
          )}
        </Box>
      )}

      {/* ── Follow-up suggestions ── */}
        {!isStreaming && (visibleFollowUps?.length > 0) && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Suggested follow-ups
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
               {visibleFollowUps.map((suggestion, idx) => (
                <Chip
                  key={idx}
                  label={suggestion}
                  size="small"
                  variant="outlined"
                  onClick={() => onFollowUpClick(suggestion)}
                  sx={{
                    fontSize: '0.78rem',
                    height: 'auto',
                    py: 0.5,
                    cursor: 'pointer',
                    borderColor: 'primary.200',
                    color: 'primary.dark',
                    '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' },
                    '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.main' },
                  }}
                />
              ))}
            </Box>
          </Box>
        </>
      )}
    {/* ── AI Governance review panel ── */}
      {!isStreaming && activeEntry && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box mb={2}>
            <ArtifactReviewPanel
              artifactType="financialIntelligence"
              artifactId={activeEntry.id}
              clientId={clientId}
              currentStatus={activeEntry.reviewStatus}
              reviewedByEmail={activeEntry.reviewedByEmail}
              reviewedAt={activeEntry.reviewedAt}
              reviewNotes={activeEntry.reviewNotes}
              reviewHistory={activeEntry.reviewHistory || []}
            />
          </Box>
        </>
      )}

      {/* ── User notes display ── */}
      {!isStreaming && activeEntry && (
        <Box mb={2}>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.75, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Your Notes
          </Typography>
          <Paper elevation={0} variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', borderColor: 'grey.200' }}>
            {activeEntry.userNotes ? (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.88rem' }}>
                {activeEntry.userNotes}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No notes added yet.
              </Typography>
            )}
          </Paper>
        </Box>
      )}

            {/* ── Feedback display ── */}
      {!isStreaming && activeEntry && (
        <Box mb={2}>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.75, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Your Feedback
          </Typography>
          <Paper elevation={0} variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', borderColor: 'grey.200' }}>
            {activeEntry.feedback ? (
              <Box>
                <Rating value={activeEntry.feedback.rating || 0} readOnly size="small" sx={{ mb: 0.5 }} />
                {activeEntry.feedback.comment && (
                  <Typography variant="body2" sx={{ fontSize: '0.88rem', whiteSpace: 'pre-wrap' }}>
                    {activeEntry.feedback.comment}
                  </Typography>
                )}
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                  Submitted by {activeEntry.feedback.submittedBy}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No feedback submitted yet.
              </Typography>
            )}
          </Paper>
        </Box>
      )}

      {/* ── Note dialog ── */}
      <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{activeEntry?.userNotes ? 'Edit Note' : 'Add Note'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Your private note"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveNote} variant="contained" disabled={savingNote}>
            {savingNote ? <CircularProgress size={18} /> : 'Save Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Share dialog ── */}
      <Dialog open={shareDialogOpen} onClose={closeShareDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Share Analysis via Email
          {activeEntry?.triggerLabel && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {activeEntry.triggerLabel.substring(0, 100)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              autoFocus
              label="Recipient email address"
              type="email"
              fullWidth
              value={shareRecipient}
              onChange={(e) => setShareRecipient(e.target.value)}
            />
            <TextField
              label="Personal message (optional)"
              multiline
              rows={3}
              fullWidth
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              placeholder="Add a note to accompany this analysis…"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeShareDialog}>Cancel</Button>
          <Button
            onClick={handleShare}
            variant="contained"
            disabled={!shareRecipient.trim() || sharing}
            startIcon={sharing ? <CircularProgress size={16} color="inherit" /> : <ShareIcon />}
          >
            {sharing ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Feedback dialog ── */}
      <Dialog open={feedbackDialogOpen} onClose={closeFeedbackDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {activeEntry?.feedback ? 'Update Feedback' : 'Leave Feedback'}
          {activeEntry?.triggerLabel && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {activeEntry.triggerLabel.substring(0, 100)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>How would you rate this analysis?</Typography>
            <Rating
              value={feedbackRating}
              onChange={(_, val) => setFeedbackRating(val)}
              size="large"
              sx={{ mb: 2 }}
            />
            <TextField
              multiline
              rows={3}
              fullWidth
              label="Additional comments (optional)"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="What did you think of this analysis? Any suggestions?"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFeedbackDialog}>Cancel</Button>
          <Button
            onClick={handleFeedbackSubmit}
            variant="contained"
            disabled={feedbackRating === 0 || submittingFeedback}
          >
            {submittingFeedback ? <CircularProgress size={20} /> : 'Submit Feedback'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default FIOutputCanvas;